import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { imageNotAvailable } from '../../../assets/images';
import ArrowLeftPagination from '../../atoms/Icons/ArrowLeftPagination';
import ArrowRightPagination from '../../atoms/Icons/ArrowRightPagination';
import '@splidejs/react-splide/css';
// CSS moved to global styles for Turbopack compatibility
import { useRouter } from 'next/router';
import ImageZoomModal from '../../atoms/ImageZoomModal';
import { IRootState } from '../../../store';

const ServiceDetailBanner = ({
  isCMS = false,
  isSticky = true,
  imageUrl,
  typeID,
  containerClassName,
  isWidthAuto = true,
  mainImageClassName = '',
}: {
  isCMS?: boolean;
  isSticky?: boolean;
  typeID?: string;
  imageUrl?: {
    detail?: any;
    imageUrl?: string;
    imageAlt?: string;
  };
  serviceType?: string;
  containerClassName?: string;
  isWidthAuto?: boolean;
  mainImageClassName?: string;
}) => {
  const isLargeScreen = useSelector((state: IRootState) => state.page.isLargeScreen);
  const { isShowBaseModel, selectedPackage } = useSelector(
    (state: IRootState) => state.servicePackage
  );
  const [selectedImageId, setSelectedImageId] = useState(null);
  const router = useRouter();
  const splideRef = useRef(null);
  const [splideInstance, setSplideInstance] = useState(null); // State to hold Splide instance
  const [isMidSizeScreen, setIsMidSizeScreen] = useState(false);

  const { isGTMReady } = useSelector((state: IRootState) => state.dataGTM) || {};

  useEffect(() => {
    if (
      typeID &&
      (typeID === 'accessories' || typeID === 'usedCar') &&
      imageUrl.detail?.length > 0
    ) {
      setSelectedImageId(imageUrl?.detail[0]?.id);
    }

    const checkScreenSize = () => {
      setIsMidSizeScreen(window.innerWidth >= 1024 && window.innerWidth < 1240);
    };

    // Initial check when the component mounts
    checkScreenSize();

    // Add an event listener to update on window resize
    window.addEventListener('resize', checkScreenSize);

    // Clean up the event listener when the component unmounts
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (typeID === 'package') {
      if (!isShowBaseModel) {
        const foundItem = imageUrl?.detail.find((item) => {
          return item.couponCode === selectedPackage.code;
        });
        setSelectedImageId(foundItem?.id || 0);
      }
    }
  }, [selectedPackage, isShowBaseModel]);

  useEffect(() => {
    if (typeof router?.query?.serviceCode === 'string') {
      const serviceCode = router?.query?.serviceCode;
      const foundItem = imageUrl?.detail?.find(
        (item: any) => item.couponCode.toUpperCase() === serviceCode?.toUpperCase()
      );

      if (foundItem) {
        const index = imageUrl?.detail
          .filter((item) => (isLargeScreen ? item : true)) // Include items with or without images depending on screen size
          .findIndex((item) => item.id === foundItem.id);

        setSelectedImageId(foundItem.id);

        if (splideInstance && index !== -1) {
          splideInstance.go(index); // Move to the adjusted index
        }
      }
    }
  }, [router, splideInstance, isLargeScreen]);

  const selectedImage = useMemo(() => {
    return imageUrl?.detail?.find((item) => item.id === selectedImageId);
  }, [selectedImageId]);

  const handleSelectImage = (id) => {
    // Find the index of the selected image
    const index = imageUrl?.detail?.findIndex((item) => item.id === id);
    const splide = splideRef.current?.splide; // Get the current Splide instance

    const clickedImageIndex = imageUrl?.detail?.findIndex((item) => item.id === id);
    if (splide) {
      splide.go(clickedImageIndex); // Move to the clicked image
      handleArrowVisibility(splide); // Update arrow visibility after the move
    }

    // Move to the correct slide in the carousel
    if (splideInstance && index !== -1) {
      splideInstance.go(index); // Move to the selected slide
    }

    setSelectedImageId(id);
  };

  useEffect(() => {
    if (!splideInstance || !imageUrl?.detail) return;

    const index = imageUrl.detail.findIndex((item) => item.id === selectedImageId);

    if (index !== -1) {
      splideInstance.go(index);
    }
  }, [selectedImageId, splideInstance]);

  const handleArrowVisibility = (splide) => {
    const perPage = 3; // Number of images per view
    const currentIndex = splide.index; // Current index of the visible slide
    const totalSlides = splide.length; // Total number of slides

    const prevArrow = document.getElementById('prevArrow');
    const nextArrow = document.getElementById('nextArrow');

    // Hide both arrows if total slides are less than or equal to perPage
    if (totalSlides <= perPage) {
      prevArrow.style.visibility = 'hidden';
      nextArrow.style.visibility = 'hidden';
      return;
    }

    // Hide the left arrow if currentIndex is within the first page (0 to perPage-1)
    if (currentIndex < perPage) {
      prevArrow.style.visibility = 'hidden';
    } else {
      prevArrow.style.visibility = 'visible';
    }

    // Hide the right arrow if currentIndex is at or beyond the last page
    if (currentIndex >= totalSlides - perPage) {
      nextArrow.style.visibility = 'hidden';
    } else {
      nextArrow.style.visibility = 'visible';
    }
  };

  const getAltImage = () => {
    if (typeID === 'usedCar') {
      return selectedImageId ? selectedImage?.altTextImage : imageUrl?.imageAlt;
    }

    return !selectedImageId ? imageUrl?.imageAlt : selectedImage?.imageAlt;
  };

  const getImageSrc = () => {
    if (!selectedImageId && !imageUrl?.imageUrl) {
      return imageNotAvailable.src;
    }

    if (typeID === 'usedCar') {
      return selectedImageId ? selectedImage?.urlImage : imageUrl?.imageUrl;
    }

    return !selectedImageId && imageUrl?.imageUrl
      ? imageUrl?.imageUrl
      : !selectedImage?.imageUrl
        ? imageNotAvailable.src
        : selectedImage?.imageUrl;
  };

  return (
    <div className={`w-full xl:w-[580px] lg:w-[450px] ${containerClassName}`}>
      <div className={`${isSticky && 'lg:sticky lg:top-[197px]'}`}>
        {/* Selected image (desktop only) */}
        <div className="hidden lg:block lg:mb-4">
          <div className="image-kupon-service-toyota">
            <ImageZoomModal
              classname="z-[200] [&_.zoom-modal-hide]:hidden [&_.zoom-modal-content]:h-full [&_.zoom-modal-content]:rounded-none"
              isWidthAuto={isWidthAuto}
            >
              <Image
                id={selectedImageId}
                alt={getAltImage()}
                width={496}
                height={248}
                priority={true}
                quality={70}
                className={`w-[600px] h-[300px] object-contain cursor-pointer aspect-video ${mainImageClassName}`}
                src={getImageSrc()}
              />
            </ImageZoomModal>
          </div>
        </div>

        <div className={`lg:hidden ${isGTMReady ? 'hidden' : 'block'}`}>
          <Image
            alt={typeID === 'accessories' ? 'Accessories Image' : 'Service Package Coupon Image'}
            width={496}
            quality={50}
            height={248}
            className="w-full h-full max-h-full object-center mx-auto aspect-video"
            src={
              typeID === 'usedCar'
                ? imageUrl?.detail[0]?.urlImage
                  ? imageUrl?.detail[0]?.urlImage
                  : imageNotAvailable.src
                : !imageUrl?.detail[0]?.imageUrl
                  ? imageNotAvailable.src
                  : imageUrl?.detail[0]?.imageUrl
            }
            priority={true}
          />
        </div>

        {typeof window === 'undefined' && (
          <noscript>
            <div className="noscript-slider">
              <div className="splide__list">
                {imageUrl?.detail?.map((item, index) => (
                  <div className="flex-shrink-0 w-24" key={index}>
                    <img
                      src={item?.imageUrl || imageNotAvailable.src}
                      alt={
                        typeID === 'accessories'
                          ? 'Accessories Image'
                          : typeID === 'usedCar'
                            ? 'Car Image'
                            : 'Service Package Coupon Image'
                      }
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </noscript>
        )}

        {
          /* Images carousel */
          (isCMS || isGTMReady) && (
            <Splide
              aria-label="Carousel"
              hasTrack={false}
              tag="section"
              options={{
                type: 'slide',
                fixedWidth: !isLargeScreen ? '100%' : '100px',
                fixedHeight: !isLargeScreen ? 'auto' : '56px',
                perMove: 3,
                perPage: !isLargeScreen ? 1 : 5,
                pagination: false,
                arrows: !isLargeScreen ? false : true,
                drag: !isLargeScreen ? true : false,
                gap: '20px',
                focus: imageUrl?.detail?.length > 5 ? 'center' : 0,
              }}
              ref={splideRef}
              onMounted={(splide) => {
                setSplideInstance(splide); // Set the Splide instance once it's mounted
                handleArrowVisibility(splide); // Initial arrow visibility check
                splide.on('moved', () => handleArrowVisibility(splide)); // Update arrow visibility on move
              }}
            >
              <SplideTrack>
                {imageUrl?.detail?.map((item, index) => (
                  <SplideSlide key={index}>
                    <div
                      className={`relative w-full h-full lg:border-4 ${item.id === selectedImageId ? 'border-supportiveRed' : 'border-transparent'}`}
                    >
                      {isLargeScreen ? (
                        <Image
                          // overrideSrc={!item?.imageUrl ? imageNotAvailable.src : item?.imageUrl}
                          alt={
                            typeID === 'accessories'
                              ? 'Accessories Image'
                              : typeID === 'usedCar'
                                ? 'Car Image'
                                : 'Service Package Coupon Image'
                          }
                          width={64}
                          height={32}
                          priority={false}
                          quality={10}
                          fetchPriority="low"
                          sizes="(min-width: 1240px) 811px, (min-width: 768) 400px, 100vw"
                          className="w-full h-full object-cover cursor-pointer aspect-video"
                          onClick={() => handleSelectImage(item.id)}
                          src={
                            typeID === 'usedCar'
                              ? item?.urlImage
                                ? item.urlImage
                                : imageNotAvailable.src
                              : !item?.imageUrl
                                ? imageNotAvailable.src
                                : item?.imageUrl
                          }
                        />
                      ) : (
                        <ImageZoomModal classname="z-[200] [&_.zoom-modal-hide]:hidden [&_.zoom-modal-content]:h-full [&_.zoom-modal-content]:rounded-none">
                          <Image
                            // overrideSrc={!item?.imageUrl ? imageNotAvailable.src : item?.imageUrl}
                            alt={
                              typeID === 'accessories'
                                ? 'Accessories Image'
                                : 'Service Package Coupon Image'
                            }
                            width={496}
                            quality={70}
                            height={248}
                            // sizes="(min-width: 425px) 811px, (min-width: 1040px) 645px, 100vw"
                            className="w-full h-full max-h-full object-center mx-auto aspect-video"
                            src={!item?.imageUrl ? imageNotAvailable.src : item?.imageUrl}
                            priority={index === 0 ? true : false}
                          />
                        </ImageZoomModal>
                      )}

                      {/* Custom pagination (mobile only) */}
                      <nav
                        className="block absolute left-2 bottom-2 px-1 py-0.5 bg-[#FFFFFF] border-[0.5px] border-[#B3B4BE] rounded lg:hidden"
                        role="navigation"
                        aria-label="Car Images Pagination"
                      >
                        <ul>
                          <li className="text-[10px] font-medium text-[#807F7F] leading-normal">
                            <span>{index + 1}</span>
                            <span>/</span>
                            <span>{imageUrl?.detail?.length}</span>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </SplideSlide>
                ))}
              </SplideTrack>

              {/* Arrows */}
              <div
                id="splideServiceBanner"
                className={`splide__arrows ${(imageUrl?.detail?.length <= 5 && !isMidSizeScreen) || (imageUrl?.detail?.length <= 4 && isMidSizeScreen) ? 'hidden' : ''}`}
              >
                <button
                  id="prevArrow"
                  className={`splide__arrow splide__arrow--prev !absolute !w-4 !h-[30px] !bg-reliableBlack !opacity-100 !rounded-none !top-[50%] !left-0 !-translate-y-1/2 !-translate-x-1/2 rotate-180 hover:!bg-supportiveRed ${isCMS ? 'pointer-events-none' : ''}`}
                  onClick={() => splideRef.current?.go('<')}
                >
                  <ArrowLeftPagination size={12} fill="#FFFEFE" />
                </button>
                <button
                  id="nextArrow"
                  className={`splide__arrow splide__arrow--next !absolute !w-4 !h-[30px] !bg-reliableBlack !opacity-100 !rounded-none !top-[50%] !right-0 !-translate-y-1/2 !translate-x-1/2 hover:!bg-supportiveRed ${isCMS ? 'pointer-events-none' : ''}`}
                  onClick={() => splideRef.current?.go('>')}
                >
                  <ArrowRightPagination size={12} fill="#FFFEFE" />
                </button>
              </div>
            </Splide>
          )
        }
      </div>
    </div>
  );
};

export default ServiceDetailBanner;
